import { NavigationMenu as NavigationMenuPrimitive } from '@base-ui-components/react/navigation-menu';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/utils';

const NavigationMenu = ({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Root>) => (
  <NavigationMenuPrimitive.Root
    delay={0}
    closeDelay={0}
    ref={ref}
    className={cn('z-10', className)}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Root>
);

const NavigationMenuList = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.List>) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={cn('h-full list-none', className)}
    {...props}
  />
);

const navigationMenuTriggerStyle = cva(
  'h-full list-none px-4 transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50'
);

const NavigationMenuItem = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Item>) => (
  <NavigationMenuPrimitive.Item
    ref={ref}
    className={cn('h-full list-none', className)}
    {...props}
  />
);

const NavigationMenuTrigger = ({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Trigger>) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn(navigationMenuTriggerStyle(), 'group', className)}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Trigger>
);

const NavigationMenuContent = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Content>) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn('w-full', className)}
    {...props}
  />
);

const NavigationMenuLink = NavigationMenuPrimitive.Link;

const NavigationMenuViewport = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Viewport>) => (
  <NavigationMenuPrimitive.Portal keepMounted>
    <NavigationMenuPrimitive.Positioner
      className="fixed top-[var(--navbar-bottom)] right-0 left-0 z-50"
      sideOffset={0}
    >
      <NavigationMenuPrimitive.Popup className="w-full overflow-hidden data-[closed]:animate-nav-height-out data-[open]:animate-nav-height-in">
        <NavigationMenuPrimitive.Viewport
          className={cn('relative w-full', className)}
          ref={ref}
          {...props}
        />
      </NavigationMenuPrimitive.Popup>
    </NavigationMenuPrimitive.Positioner>
  </NavigationMenuPrimitive.Portal>
);

const NavigationMenuIndicator = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Arrow>) => (
  <NavigationMenuPrimitive.Arrow
    ref={ref}
    className={cn(
      'data-[closed]:fade-out data-[open]:fade-in top-full z-[1] data-[closed]:animate-out data-[open]:animate-in',
      className
    )}
    {...props}
  >
    <div className="relative" />
  </NavigationMenuPrimitive.Arrow>
);

// Base UI doesn't have a Sub component, so we'll create a wrapper that uses nested Root
const NavigationMenuSub = NavigationMenuPrimitive.Root;

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuSub,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  NavigationMenuViewport,
};
